/**
 * busca-global-store — hook React Query da busca global da TopBar
 * (P2 / AJUSTES-POS-2).
 *
 * `useBuscaGlobal(termo)` chama `GET /api/v1/busca-global?q=` e devolve os
 * resultados tipados (cliente / card / os). O debounce e o gating por tamanho
 * ficam no componente (TopBar) — aqui só habilitamos a query com termo >= 2.
 */

import { useQuery } from '@tanstack/react-query';

import { api, hasToken } from './api';

/**
 * S-1 (BUSCA-UNICA): 'config' entrou no contrato. Os itens de Configurações
 * NÃO vêm do servidor: o catálogo (`configuracoes/catalogo.ts`) é estático no
 * cliente e é a fonte única — a TopBar o consulta direto (sinônimos e
 * permissão inclusos) e mistura com os resultados de dados deste endpoint.
 */
export type BuscaTipo = 'config' | 'cliente' | 'lead' | 'card' | 'os' | 'ajuda';

export interface BuscaResultado {
  tipo: BuscaTipo;
  id: string;
  titulo: string;
  subtitulo: string | null;
  href: string;
}

interface BuscaGlobalResponse {
  resultados: BuscaResultado[];
}

export function useBuscaGlobal(termo: string) {
  const q = termo.trim();
  return useQuery({
    queryKey: ['busca-global', q],
    queryFn: () =>
      api.get<BuscaGlobalResponse>(`/api/v1/busca-global?q=${encodeURIComponent(q)}`),
    enabled: hasToken() && q.length >= 2,
    // Resultados de busca envelhecem rápido, mas 30s evita refetch a cada foco.
    staleTime: 30_000,
    // Mantém os resultados anteriores enquanto o novo termo carrega (menos flicker).
    placeholderData: (prev) => prev,
  });
}
